import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { Globe, Check, ChevronRight, Sparkles } from 'lucide-react';

// Flag image component using flagcdn
const FlagImage = ({ countryCode, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-4',
    md: 'w-8 h-6',
    lg: 'w-10 h-7',
    xl: 'w-12 h-9'
  };
  
  if (!countryCode) {
    return <Globe className={`${sizeClasses[size]} text-slate-400`} />;
  }
  
  return (
    <img 
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
      alt={countryCode}
      className={`${sizeClasses[size]} object-cover rounded`}
      loading="lazy"
    />
  );
};

// All 20 Spearhead languages with country codes for flags
const SPEARHEAD_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', countryCode: 'gb' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', countryCode: 'es' },
  { code: 'fr', name: 'French', nativeName: 'Français', countryCode: 'fr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', countryCode: 'de' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', countryCode: 'br' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', countryCode: 'ru' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', countryCode: 'cn' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', countryCode: 'jp' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', countryCode: 'kr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', countryCode: 'sa' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', countryCode: 'in' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', countryCode: 'tr' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', countryCode: 'vn' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', countryCode: 'th' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', countryCode: 'id' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', countryCode: 'ir' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', countryCode: 'ke' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', countryCode: 'it' },
  { code: 'ps', name: 'Pashto', nativeName: 'پښتو', countryCode: 'af' },
  { code: 'xh', name: 'Xhosa', nativeName: 'isiXhosa', countryCode: 'za' },
];

// Languages available for English learners (when interface is in these languages)
const ENGLISH_LEARNER_TARGETS = ['en', 'ru', 'ar', 'zh', 'fa', 'fr'];

// Interface languages available
const INTERFACE_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', countryCode: 'gb' },
  { code: 'fr', name: 'French', nativeName: 'Français', countryCode: 'fr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', countryCode: 'ru' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', countryCode: 'cn' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', countryCode: 'ir' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', countryCode: 'sa' },
];

export default function ForgeLanguageSelection() {
  const navigate = useNavigate();
  const { t, locale, setLocale, isRTL } = useI18n();
  
  const [selectedInterface, setSelectedInterface] = useState(locale);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [step, setStep] = useState(1); // 1: interface, 2: target language

  // Determine available target languages based on interface language
  const availableTargets = selectedInterface === 'en' 
    ? SPEARHEAD_LANGUAGES // All 20 for English interface
    : SPEARHEAD_LANGUAGES.filter(l => ENGLISH_LEARNER_TARGETS.includes(l.code)); // Limited for non-English

  useEffect(() => {
    // Reset target selection when interface changes
    setSelectedTarget(null);
  }, [selectedInterface]);

  const handleInterfaceSelect = (code) => {
    setSelectedInterface(code);
    setLocale(code);
    // Auto-advance to target language step
    setTimeout(() => setStep(2), 200);
  };

  const handleTargetSelect = (code) => {
    setSelectedTarget(code);
    // Auto-navigate to Forge Path
    localStorage.setItem('targetLanguage', code);
    localStorage.setItem('interfaceLanguage', selectedInterface);
    setTimeout(() => navigate(`/forge/${code}?course=1`), 200);
  };

  const handleStartLearning = () => {
    if (selectedTarget) {
      // Save selection
      localStorage.setItem('targetLanguage', selectedTarget);
      localStorage.setItem('interfaceLanguage', selectedInterface);
      // Navigate to Forge Path
      navigate(`/forge/${selectedTarget}?course=1`);
    }
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className="pt-8 pb-4 px-4 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="p-2 bg-amber-500/20 rounded-xl">
            <Sparkles className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold text-white">{t('forge.title')}</h1>
        </div>
        <p className="text-slate-400">{t('forge.subtitle')}</p>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step >= 1 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'}`}>
            <Globe className="w-5 h-5" />
            <span className="font-medium">{t('languageSelection.interfaceLanguage')}</span>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-500" />
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step >= 2 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'}`}>
            <span className="font-medium">{t('languageSelection.learningLanguage')}</span>
          </div>
        </div>

        {/* Step 1: Interface Language */}
        {step === 1 && (
          <div className="animate-fadeIn">
            <h2 className="text-xl font-semibold text-white mb-4 text-center">
              {t('languageSelection.interfaceLanguage')}
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {INTERFACE_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleInterfaceSelect(lang.code)}
                  className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                    selectedInterface === lang.code
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <FlagImage countryCode={lang.countryCode} size="lg" />
                    <div className="text-left">
                      <div className="font-medium text-white">{lang.nativeName}</div>
                      <div className="text-sm text-slate-400">{lang.name}</div>
                    </div>
                  </div>
                  {selectedInterface === lang.code && (
                    <div className="absolute top-2 right-2 p-1 bg-amber-500 rounded-full">
                      <Check className="w-4 h-4 text-slate-900" />
                    </div>
                  )}
                </button>
              ))}
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setStep(2)}
                className="px-8 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-xl transition-colors flex items-center gap-2"
              >
                {t('common.next')}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Target Language */}
        {step === 2 && (
          <div className="animate-fadeIn">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setStep(1)}
                className="text-slate-400 hover:text-white transition-colors flex items-center gap-1"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                {t('common.back')}
              </button>
              <h2 className="text-xl font-semibold text-white">
                {t('languageSelection.learningLanguage')}
              </h2>
              <div className="w-16"></div>
            </div>

            {/* Info box for non-English interface */}
            {selectedInterface !== 'en' && (
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-300 text-sm text-center">
                {t('languageSelection.availableForEnglish')}
              </div>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {availableTargets.map((lang) => {
                const isAvailable = selectedInterface === 'en' || ENGLISH_LEARNER_TARGETS.includes(lang.code);
                const isComingSoon = !isAvailable;
                
                return (
                  <button
                    key={lang.code}
                    onClick={() => !isComingSoon && handleTargetSelect(lang.code)}
                    disabled={isComingSoon}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                      isComingSoon
                        ? 'border-slate-700/50 bg-slate-800/30 opacity-50 cursor-not-allowed'
                        : selectedTarget === lang.code
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <FlagImage countryCode={lang.countryCode} size="xl" />
                      <div className="text-center">
                        <div className="font-medium text-white text-sm">{lang.nativeName}</div>
                        <div className="text-xs text-slate-400">{lang.name}</div>
                      </div>
                    </div>
                    {selectedTarget === lang.code && (
                      <div className="absolute top-2 right-2 p-1 bg-amber-500 rounded-full">
                        <Check className="w-4 h-4 text-slate-900" />
                      </div>
                    )}
                    {isComingSoon && (
                      <div className="absolute bottom-1 left-0 right-0 text-center">
                        <span className="text-xs text-slate-500">{t('languageSelection.comingSoon')}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleStartLearning}
                disabled={!selectedTarget}
                className={`px-8 py-3 font-semibold rounded-xl transition-colors flex items-center gap-2 ${
                  selectedTarget
                    ? 'bg-amber-500 hover:bg-amber-400 text-slate-900'
                    : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                }`}
              >
                {t('languageSelection.startLearning')}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
